/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { EventEmitter } from 'events';

/** EventEmitter is available in node, but not on the browser,
 * this class attempts to polyfill that so the action context can remain Isomorphic
 */
/* @internal*/

/**
 * Type for event emitter listener callback
 */
export type ListenerCallback<T extends unknown[] = unknown[]> = (...args0: T) => void;

/**
 * Generic cast type
 */
export type GenericCallback = (...args: any) => void;

/**
 * Custom isomorphic event emitter implementation
 *
 * @export
 * @class CustomEventEmitter
 */
class CustomEventEmitter {
    private listeners: Map<string, ListenerCallback[]> = new Map<string, ListenerCallback[]>();
    private eventEmitter: EventEmitter | null = typeof window !== undefined ? null : new EventEmitter();

    public on<T extends unknown[] = unknown[]>(event: string, callback: ListenerCallback<T>): void {
        if (!!this.eventEmitter) {
            this.eventEmitter.on(event, <GenericCallback>callback);
        } else {
            let listeners = this.listeners.get(event);
            if (!!!listeners) {
                listeners = [];
                this.listeners.set(event, listeners);
            }

            listeners.push(<ListenerCallback>callback);
        }
    }

    public emit<T extends unknown[] = []>(event: string, ...args: T): boolean {
        if (!!this.eventEmitter) {
            return this.eventEmitter.emit(event, args);
        }

        const listeners = this.listeners.get(event);

        if (listeners && listeners.length) {
            listeners.forEach(listener => {
                listener(...args);
            });
            return true;
        }

        return false;
    }

    public removeAllListeners(event: string): void {
        if (!!this.eventEmitter) {
            this.eventEmitter.removeAllListeners(event);
        } else {
            this.listeners.delete(event);
        }
    }
}

export default new CustomEventEmitter();
